/* global window */
import React, { useCallback, useEffect, useState, useRef, useContext, forwardRef, useImperativeHandle, startTransition } from 'react';
import '../styles/SearchBox.css';
import SearchResultsGroup from './SearchResultsGroup';
import SearchResultsItemCollection from './SearchResultsItemCollection';
import SearchResultsItemProduct from './SearchResultsItemProduct';
import { useNavigate, useSearchParams } from 'react-router-dom';
import useIsRendered from '../hooks/useIsRendered';
import { LoadingContext } from './context/LoadingContext';
import getPricesFromQuery from '../helpers/getPricesFromQuery';
import Stack from 'react-bootstrap/Stack';
import PriceFilterSuggestion from './PriceFilterSuggestion';
import constants from '../constants.json' assert { type: 'json' };

const DEFAULT_NON_PAGE_LIMIT = 4;
const TIMEOUT_DURATION = 600;
const MIN_SEARCH_LENGTH = 3;
const AUTO_SEARCH_WAIT_PAGE_MS = 400;
const AUTO_SEARCH_WAIT_NON_PAGE_MS = 600;

// eslint-disable-next-line
function SearchBox({ isMobile, isPage = false, productList = [], setProductList = () => { }, searchLoaded = false, setSearchLoaded = () => { }, setSearchLoading = () => { }, searchQuery = {}, setSearchQuery = () => { }, setPagesData = () => { }, setExpanded = () => {} }, searchBoxRef) {
    const isRendered = useIsRendered();
    const navigate = useNavigate();
    const searchBox = useRef(null);
    const [searchExpanded, setSearchExpanded] = useState(false);
    const [collectionList, setCollectionList] = useState([]);
    const [prices, setPrices] = useState({
        minPrice: null,
        maxPrice: null,
    });
    const [searchParams, setParams] = useSearchParams();
    const [alterPath, setAlterPath] = useState(false);

    useImperativeHandle(searchBoxRef, () => ({
        enableAlterPath() {
            setAlterPath(true);
        }
    }), []);

    // Loading state
    const {
        startLoading,
        finishLoading,
    } = useContext(LoadingContext);
    const handleSearch = useCallback(async (isFirstRender = false) => {
        try {
            if (searchQuery.q?.length < MIN_SEARCH_LENGTH
                && searchQuery.v?.length < MIN_SEARCH_LENGTH) {
                return;
            }
            setSearchLoading(true);
            startLoading();
            const commonParams = new URLSearchParams(Object.assign({}, ...Object.entries(searchQuery).filter(([_k, v]) => !Array.isArray(v) ? Boolean(v) : v.length > 0).map(([k, v]) => ({ [k]: v })), {
                ...!isPage === true && ({ limit: DEFAULT_NON_PAGE_LIMIT })
            })).toString();
            const productParams = commonParams + `&fields=${constants.MINIMAL_PRODUCT_FIELDS.join(',')}`;
            const collectionParams = commonParams + `&fields=${constants.MINIMAL_COLLECTION_FIELDS.join(',')}`;
            // Fetch data
            const [collectionResponse, productResponse] = await Promise.all([
                fetch(new URL('api/collections?' + collectionParams, process.env.APP_URL)),
                fetch(new URL('api/products/filter?' + productParams, process.env.APP_URL)),
            ]);
            // Collections
            const collectionData = await collectionResponse.json();
            setCollectionList(collectionData.collections ?? []);
            // Products
            const productData = await productResponse.json();
            setProductList(productData.products ?? []);
            if (productData.pageInfo) {
                setPagesData({
                    limit: productData.pageInfo.limitPerPage,
                    count: productData.pageInfo.totalObjects,
                    pages: productData.pageInfo.totalPages,
                    availableTagPrefixes: productData.pageInfo.availableTagPrefixes ?? [],
                    availableTagFilters: productData.pageInfo.availableTagFilters ?? [],
                    availableVendors: productData.pageInfo.availableVendors ?? [],
                    productMaxPrice: productData.pageInfo.productMaxPrice,
                });
            }
            // Update Search
            setSearchLoaded(true);
            setSearchLoading(false);
            finishLoading();
            if (!isFirstRender && !isPage) {
                setSearchExpanded(true);
            }
        } catch (e) {
            setSearchLoading(false);
            setSearchLoaded(false);
            finishLoading();
        }
    }, [searchQuery, isPage]);
    useEffect(() => {
        let autoSearchTimer = null;
        const AUTO_SEARCH_WAIT_MS = isPage ? AUTO_SEARCH_WAIT_PAGE_MS : AUTO_SEARCH_WAIT_NON_PAGE_MS;
        autoSearchTimer = setTimeout(() => handleSearch(!isRendered), AUTO_SEARCH_WAIT_MS);
        if (!searchQuery.q) {
            startTransition(() => {
                setSearchExpanded(false);
            });
        }
        return () => {
            clearTimeout(autoSearchTimer);
        };
    }, [handleSearch]);
    useEffect(() => {
        // Detect outside clicks
        const detectOutsideClick = (e) => {
            if (searchBox.current
                && !searchBox.current.contains(e.target)
                && searchExpanded === true) {
                setSearchExpanded(false);
            }
        };
        window.addEventListener('click', detectOutsideClick, true);
        return () => {
            window.removeEventListener('click', detectOutsideClick, true);
        };
    }, [searchExpanded]);
    const handleSearchButtonPress = function() {
        if (!searchQuery.q) {
            return;
        }

        if (isMobile) {
            setExpanded(false);
        }

        if (!isPage) {
            return navigate(genResultsUrl());
        }
        return handleSearch();
    };
    const genResultsUrl = function() {
        return `/searchresults.asp?${new URLSearchParams(Object.assign({}, ...Object.entries(searchQuery).filter(([_k, v]) => !Array.isArray(v) ? Boolean(v) : v.length > 0).map(([k, v]) => ({ [k]: v })), { page: 0 })).toString()}`;
    };
    const handleSearchKeyPress = function(e) {
        if (!searchQuery.q) {
            return;
        }

        if (e.key === 'Enter') {
            if (isMobile) {
                setExpanded(false);
            }

            if (!isPage) {
                return navigate(genResultsUrl());
            }
            return handleSearch();
        }
    };

    const setPricesWrapper = function(updates) {
        setPrices((prevPrices) => ({
            ...prevPrices,
            ...updates,
        }));
    };

    const onQueryChange = function(e) {
        setSearchQuery({
            ...searchQuery,
            q: e.target.value,
        });
    };

    const onPriceSuggestionAccept = function(price) {
        let priceObject = prices[price];

        if (!priceObject) {
            priceObject = {};
        }

        setSearchQuery({
            ...searchQuery,
            q: prices[price].newQuery,
            [price]: priceObject.value,
        });

        setPricesWrapper({ [price]: null });

        if (alterPath) {
            const currentParams = Object.fromEntries(searchParams.entries());
            const updatedParams = {
                ...currentParams,
                [price]: priceObject.value
            };

            setParams(updatedParams);
        }
    };

    const handleMinPriceSuggestionClick = useCallback(() => onPriceSuggestionAccept('minPrice'), [onPriceSuggestionAccept]);
    const handleMaxPriceSuggestionClick = useCallback(() => onPriceSuggestionAccept('maxPrice'), [onPriceSuggestionAccept]);
    const handleSetSearchExpandedFalse = useCallback(() => setSearchExpanded(false), [setSearchExpanded]);
    const handleInputFocus = useCallback(() => setSearchExpanded(true), [setSearchExpanded]);

    const renderCollection = function(collection) {
        return (
            <SearchResultsItemCollection
                onClick={handleSetSearchExpandedFalse}
                key={collection.id}
                title={collection.title}
                query={searchQuery.q}
                minPrice={searchQuery.minPrice}
                maxPrice={searchQuery.maxPrice}
                handle={collection.handle}
            />
        );
    };

    const renderProduct = function(product) {
        return (
            <SearchResultsItemProduct
                onClick={handleSetSearchExpandedFalse}
                key={product.id}
                title={product.title}
                handle={product.handle}
                query={searchQuery.q}
                imageUrl={product.imageUrl}
            />
        );
    };

    // Set minPrice, maxPrice from search query
    useEffect(() => {
        const timeoutId = setTimeout(() => {
            const { minPrice: oldMinPrice, maxPrice: oldMaxPrice } = searchQuery;
            const { minPrice, maxPrice } = getPricesFromQuery(searchQuery.q);

            if (minPrice && oldMaxPrice && parseFloat(oldMaxPrice) > parseFloat(minPrice.value)) {
                setPricesWrapper({ minPrice });
            } else {
                setPricesWrapper({ minPrice: null });
            }

            if (maxPrice && oldMinPrice && parseFloat(oldMinPrice) < parseFloat(maxPrice.value)) {
                setPricesWrapper({ maxPrice });
            } else {
                setPricesWrapper({ maxPrice: null });
            }

            if (!oldMaxPrice && minPrice) {
                setPricesWrapper({ minPrice });
            }

            if (!oldMinPrice && maxPrice) {
                setPricesWrapper({ maxPrice });
            }
        }, TIMEOUT_DURATION);

        return () => clearTimeout(timeoutId);
    }, [searchQuery]);

    const { minPrice, maxPrice } = prices;

    // Sort products to prioritize those with images
    const sortedProductList = productList ? [...productList].sort((a, b) => {
        if (a.imageUrl && !b.imageUrl) {
            return -1;
        }
        if (a.imageUrl && b.imageUrl) {
            return a.id - b.id;
        }
        if (!a.imageUrl && b.imageUrl) {
            return 1;
        }
    }) : productList;

    return (<div
        ref={searchBox}
        className={'search-box'}>
        <input
            type={'text'}
            placeholder={'Enter Keyword or Part Number...'}
            value={searchQuery.q}
            onChange={onQueryChange}
            onFocus={handleInputFocus}
            onKeyDown={handleSearchKeyPress} />
        <button
            type={'submit'}
            aria-label={'Search'}
            onClick={handleSearchButtonPress}>
            Search
        </button>
        {(searchLoaded && !isPage) && (<div
            className={'search--results'}
            style={{ ...searchExpanded === true ? ({ display: 'flex' }) : ({ display: 'none' }) }}>
            <Stack
                direction={'horizontal'}
                className={'py-3 px-2'}
                gap={1}
            >
                {minPrice ? (
                    <PriceFilterSuggestion
                        onClick={handleMinPriceSuggestionClick}
                        matchedString={minPrice.matchedString}
                    />
                ): null}
                {maxPrice ? (
                    <PriceFilterSuggestion
                        onClick={handleMaxPriceSuggestionClick}
                        matchedString={maxPrice.matchedString}
                    />
                ): null}
            </Stack>
            {!collectionList.length && !productList.length && (<p
                style={{ padding: '5px 10px',
                    marginTop: '1em' }}>No results found.</p>)}
            {collectionList.length > 0 && (<SearchResultsGroup
                title={'Categories'}>
                {collectionList.map(renderCollection)}
            </SearchResultsGroup>)}
            {productList.length > 0 && (<SearchResultsGroup
                title={'Products'}>
                {sortedProductList.map(renderProduct)}
            </SearchResultsGroup>)}
        </div>)}
    </div>);
}

export default forwardRef(SearchBox);